/**
 * DayView.js
 *
 * Copyright (c) 2026 Eric Seigne <eric.seigne@cap-rel.fr>
 *
 * This program is free software: you can redistribute it and/or modify
 * it under the terms of the GNU Affero General Public License as
 * published by the Free Software Foundation, either version 3 of the
 * License, or (at your option) any later version.
 *
 * This program is distributed in the hope that it will be useful,
 * but WITHOUT ANY WARRANTY; without even the implied warranty of
 * MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
 * GNU Affero General Public License for more details.
 *
 * You should have received a copy of the GNU Affero General Public License
 * along with this program.  If not, see <https://www.gnu.org/licenses/>.
 */

import { planDay, chainedPlan } from '../core/DayPlanner.js'
import { PlanError } from '../core/errors.js'
import { i18n } from '../i18n/index.js'
import {
  formatDuration,
  surfaceIntervalSentences,
  noFlyValue,
  exposureShown
} from '../utils/planUtils.js'
import { escapeHtml, showToast } from '../utils/viewUtils.js'
import { DayExport } from '../utils/DayExport.js'
import { SavedDays } from '../utils/SavedDays.js'

// The day of diving: the dives planned one after the other, each starting from
// the tissues the previous one left.
//
// This screen owns the series and nothing else. It never plans a dive itself:
// it hands the day to DayPlanner.planDay and stores what comes back, so a dive
// of the series and a dive of the plan screen are computed by the same code.
// Editing the segments of a dive is delegated to the plan screen, which is the
// one editor of a dive in this application (Store.startEditingDive).
export class DayView {
  // Dialogs of this view. A redraw would tear them out of the DOM with the
  // name being typed into them, so subscribe() waits until they are closed.
  static MODAL_IDS = Object.freeze(['day-interval-modal', 'day-save-modal', 'day-delete-modal'])

  constructor(container) {
    this.container = container
    this.unsubscribe = null
    // Dive being deleted, kept between the click and the confirmation
    this.pendingDeleteIndex = null
  }

  t(key, params) {
    return i18n.t(key, params)
  }

  destroy() {
    if (this.unsubscribe) {
      this.unsubscribe()
      this.unsubscribe = null
    }
    this.container = null
    this.pendingDeleteIndex = null
  }

  render() {
    const dives = window.store.state.day.dives

    this.container.innerHTML = `
      <div class="space-y-4 pb-20">
        <div class="card bg-base-100 shadow-sm">
          <div class="card-body p-4">
            <div class="flex justify-between items-center">
              <h2 class="card-title text-sm">${this.t('day_title')}</h2>
              <div class="flex gap-1">
                <button id="day-add-current-btn" class="btn btn-ghost btn-xs">${this.t('day_add_current')}</button>
                <button id="day-saved-btn" class="btn btn-ghost btn-xs">${this.t('saved_days_title')}</button>
                ${dives.length > 0 ? `<button id="day-clear-btn" class="btn btn-ghost btn-xs text-error">${this.t('day_clear')}</button>` : ''}
              </div>
            </div>

            ${dives.length === 0 ? this.renderEmpty() : `
              <div class="space-y-2 mt-2">
                ${dives.map((dive, index) => this.renderDive(dive, index)).join('')}
              </div>
              <div class="flex gap-2 mt-3">
                <button id="day-calculate-btn" class="btn btn-primary btn-sm flex-1">${this.t('day_calculate')}</button>
                <button id="day-pdf-btn" class="btn btn-ghost btn-sm">${this.t('plan_export_pdf')}</button>
                <button id="day-html-btn" class="btn btn-ghost btn-sm">${this.t('plan_save_html')}</button>
                <button id="day-save-btn" class="btn btn-secondary btn-sm">${this.t('saved_days_save')}</button>
              </div>
              ${this.renderDaySummary(dives)}
            `}
          </div>
        </div>
      </div>

      <dialog id="day-interval-modal" class="modal">
        <div class="modal-box">
          <h3 class="font-bold text-lg">${this.t('day_add_current')}</h3>
          <div class="form-control mt-4">
            <label class="label"><span class="label-text">${this.t('day_ask_surface_interval')}</span></label>
            <input type="number" id="day-interval-value" class="input input-bordered" min="0" step="5" value="120" />
            <p id="day-interval-error" class="text-error text-xs mt-2 hidden">${this.t('day_invalid_surface_interval')}</p>
          </div>
          <div class="modal-action">
            <button id="day-cancel-interval-btn" class="btn btn-ghost">${this.t('cancel')}</button>
            <button id="day-confirm-interval-btn" class="btn btn-primary">${this.t('add')}</button>
          </div>
        </div>
        <form method="dialog" class="modal-backdrop"><button>close</button></form>
      </dialog>

      <dialog id="day-save-modal" class="modal">
        <div class="modal-box">
          <h3 class="font-bold text-lg">${this.t('saved_days_save')}</h3>
          <div class="form-control mt-4">
            <label class="label"><span class="label-text">${this.t('saved_plans_name_label')}</span></label>
            <input type="text" id="day-save-name" class="input input-bordered" placeholder="${this.t('saved_days_name_placeholder')}" />
          </div>
          <div class="modal-action">
            <button id="day-cancel-save-btn" class="btn btn-ghost">${this.t('cancel')}</button>
            <button id="day-confirm-save-btn" class="btn btn-primary">${this.t('save')}</button>
          </div>
        </div>
        <form method="dialog" class="modal-backdrop"><button>close</button></form>
      </dialog>

      <dialog id="day-delete-modal" class="modal">
        <div class="modal-box">
          <h3 class="font-bold text-lg">${this.t('day_remove_dive')}</h3>
          <p class="py-4 text-sm">${this.t('day_confirm_remove')}</p>
          <div class="modal-action">
            <button id="day-cancel-delete-btn" class="btn btn-ghost">${this.t('cancel')}</button>
            <button id="day-confirm-delete-btn" class="btn btn-error">${this.t('delete')}</button>
          </div>
        </div>
        <form method="dialog" class="modal-backdrop"><button>close</button></form>
      </dialog>
    `

    this.bindEvents()
    this.subscribe()
  }

  // Redraw when the day changes from somewhere else - the plan screen saving a
  // dive back into the series, the settings dropping every computed dive.
  //
  // Two guards, both of which cost a lost edit when they are missing: render()
  // rewrites the whole container, so it would tear a dialog out of the DOM and
  // it would wipe the field being typed into.
  subscribe() {
    if (this.unsubscribe) return
    this.unsubscribe = window.store.subscribe(() => {
      if (!this.container) return
      if (DayView.MODAL_IDS.some(id => document.getElementById(id)?.open)) return
      const active = document.activeElement
      if (active && this.container.contains(active) && active.tagName === 'INPUT') return
      this.render()
    })
  }

  renderEmpty() {
    return `
      <div class="text-center py-8 opacity-70">
        <svg xmlns="http://www.w3.org/2000/svg" class="h-12 w-12 mx-auto mb-3 opacity-50" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
        </svg>
        <p>${this.t('day_empty')}</p>
        <p class="text-xs mt-1">${this.t('day_empty_hint')}</p>
      </div>
    `
  }

  // Surface interval before a dive, editable. The first dive of the day
  // follows nothing, so it has no field at all rather than an empty one.
  renderSurfaceInterval(dive, index) {
    if (index === 0) return ''

    const minutes = dive.surfaceIntervalBefore ?? 0
    return `
      <div class="flex items-center gap-2 pl-3 py-1 text-xs opacity-80">
        <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4 opacity-60" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
        <span>${this.t('day_surface_interval')}</span>
        <input type="number" min="0" step="5" value="${minutes}"
          class="input input-bordered input-xs w-20 day-interval-input" data-index="${index}" />
        <span>${this.t('unit_minutes')}</span>
        <span class="opacity-60">(${formatDuration(minutes, this.t.bind(this))})</span>
      </div>
    `
  }

  // One dive of the series: what it is, and what it costs once computed.
  renderDive(dive, index) {
    const plan = chainedPlan(dive.results)
    const editing = window.store.state.day.editingIndex === index
    const mode = dive.ccr?.enabled ? 'CCR' : 'OC'
    const depth = Math.max(0, ...dive.segments.map(s => s.depth || 0))
    const time = dive.segments.reduce((sum, s) => sum + (s.time || 0), 0)

    return `
      ${this.renderSurfaceInterval(dive, index)}
      <div class="bg-base-200 rounded-lg p-3 ${editing ? 'ring-1 ring-primary' : ''}">
        <div class="flex justify-between items-start gap-2">
          <div class="flex-1 min-w-0">
            <div class="flex items-center gap-2">
              <span class="badge badge-sm">${index + 1}</span>
              <input type="text" value="${escapeHtml(dive.label || '')}" placeholder="${this.t('day_dive_label_placeholder')}"
                class="input input-ghost input-xs flex-1 min-w-0 day-label-input" data-index="${index}" />
            </div>
            <div class="text-xs opacity-70 mt-1">
              ${depth} m | ${Math.round(time)} min | ${mode}
            </div>
            ${this.renderDiveResult(dive, plan)}
          </div>
          <div class="flex flex-col gap-1">
            <button class="btn btn-ghost btn-xs day-edit-btn" data-index="${index}">${this.t('edit')}</button>
            <button class="btn btn-ghost btn-xs text-error day-remove-btn" data-index="${index}">${this.t('delete')}</button>
          </div>
        </div>
      </div>
    `
  }

  // What the diver reads off a computed dive, or why he reads nothing.
  //
  // Three states are told apart on purpose: never computed, computed then
  // invalidated by an edit (dive.stale), and refused by the engine. Showing an
  // empty line for all three would leave a diver believing his series is up to
  // date when it no longer is.
  renderDiveResult(dive, plan) {
    if (dive.error) {
      return `<div class="text-xs text-error mt-1">${this.t('day_dive_refused')}</div>`
    }
    if (!plan) {
      const key = dive.stale ? 'day_dive_stale' : 'day_dive_not_computed'
      return `<div class="text-xs opacity-60 mt-1">${this.t(key)}</div>`
    }

    const exposure = exposureShown(plan)
    const stops = plan.stops.filter(s => s.time > 0)
    const stopText = stops.length
      ? stops.map(s => `${s.depth}m/${s.time}'`).join(' ')
      : this.t('plan_no_deco')

    return `
      <div class="text-xs mt-1">
        <span class="opacity-70">${this.t('plan_tts')}</span> ${plan.tts + 1} min
        <span class="opacity-70 ml-2">${this.t('plan_runtime')}</span> ${Math.ceil(plan.runtime) + 1} min
        <span class="opacity-70 ml-2">${this.t(exposure.series ? 'plan_cns_series' : 'plan_cns')}</span>
        <span class="${exposure.cnsWarning ? 'text-warning' : ''}">${exposure.cns}%</span>
      </div>
      <div class="text-xs opacity-70 mt-1">${this.t('plan_stops_summary')} : ${stopText}</div>
    `
  }

  // Totals of the day, read off the last computed dive: the oxygen clock and
  // the surface interval information are cumulative, so the last dive of the
  // series carries the state the diver leaves the water with.
  renderDaySummary(dives) {
    const computed = dives.map(d => chainedPlan(d.results)).filter(Boolean)
    if (computed.length === 0) return ''

    const last = computed[computed.length - 1]
    const exposure = exposureShown(last)
    const t = this.t.bind(this)
    const { desat, danCase, danRule, danSource } = surfaceIntervalSentences(last, t)
    const noFly = noFlyValue(last, t)
    const sentences = [desat, danCase, danRule, danSource].filter(Boolean).join(' ')

    // Said out loud when the series is not fully computed: the totals below
    // cover the dives that were, and no others.
    const partial = computed.length < dives.length
      ? `<p class="text-xs text-warning mt-2 px-1">${this.t('day_summary_partial', { computed: computed.length, total: dives.length })}</p>`
      : ''

    return `
      <div class="divider text-xs">${this.t('day_summary')}</div>
      <div class="stats stats-horizontal shadow w-full text-center">
        <div class="stat px-2 py-2">
          <div class="stat-title text-xs">${this.t('day_dives_count')}</div>
          <div class="stat-value text-base">${computed.length}</div>
        </div>
        <div class="stat px-2 py-2">
          <div class="stat-title text-xs">${this.t('plan_cns_series')}</div>
          <div class="stat-value text-base ${exposure.cnsWarning ? 'text-warning' : ''}">${exposure.cns}%</div>
        </div>
        <div class="stat px-2 py-2">
          <div class="stat-title text-xs">${this.t('plan_otu_series')}</div>
          <div class="stat-value text-base ${exposure.otuWarning ? 'text-warning' : ''}">${exposure.otu}</div>
        </div>
        ${noFly ? `
          <div class="stat px-2 py-2">
            <div class="stat-title text-xs">${this.t('plan_no_fly')}</div>
            <div class="stat-value text-base">${noFly}</div>
          </div>
        ` : ''}
      </div>
      ${partial}
      ${sentences ? `<p class="text-xs opacity-70 mt-2 px-1">${sentences}</p>` : ''}
    `
  }

  bindEvents() {
    document.getElementById('day-add-current-btn')?.addEventListener('click', () => this.addCurrentDive())
    document.getElementById('day-calculate-btn')?.addEventListener('click', () => this.calculate())

    document.getElementById('day-cancel-interval-btn')?.addEventListener('click', () => {
      document.getElementById('day-interval-modal')?.close()
    })

    document.getElementById('day-confirm-interval-btn')?.addEventListener('click', () => this.confirmAddCurrentDive())

    // Enter validates, like any other form of the application
    document.getElementById('day-interval-value')?.addEventListener('keydown', e => {
      if (e.key === 'Enter') {
        e.preventDefault()
        this.confirmAddCurrentDive()
      }
    })

    document.getElementById('day-saved-btn')?.addEventListener('click', () => {
      window.location.hash = '/saved'
    })

    document.getElementById('day-save-btn')?.addEventListener('click', () => {
      const input = document.getElementById('day-save-name')
      if (input) input.value = SavedDays.generateName(window.store.state)
      document.getElementById('day-save-modal')?.showModal()
    })

    document.getElementById('day-cancel-save-btn')?.addEventListener('click', () => {
      document.getElementById('day-save-modal')?.close()
    })

    document.getElementById('day-confirm-save-btn')?.addEventListener('click', () => this.save())

    document.getElementById('day-pdf-btn')?.addEventListener('click', () => this.export('pdf'))
    document.getElementById('day-html-btn')?.addEventListener('click', () => this.export('html'))

    document.getElementById('day-clear-btn')?.addEventListener('click', () => {
      if (confirm(this.t('day_confirm_clear'))) {
        window.store.clearDay()
        this.render()
      }
    })

    this.container.querySelectorAll('.day-edit-btn').forEach(btn => {
      btn.addEventListener('click', e => {
        const index = parseInt(e.currentTarget.dataset.index, 10)
        if (window.store.startEditingDive(index)) {
          window.location.hash = '/'
        }
        // Store.startEditingDive logged the reason when it refused
      })
    })

    this.container.querySelectorAll('.day-remove-btn').forEach(btn => {
      btn.addEventListener('click', e => {
        this.pendingDeleteIndex = parseInt(e.currentTarget.dataset.index, 10)
        document.getElementById('day-delete-modal')?.showModal()
      })
    })

    document.getElementById('day-cancel-delete-btn')?.addEventListener('click', () => {
      this.pendingDeleteIndex = null
      document.getElementById('day-delete-modal')?.close()
    })

    document.getElementById('day-confirm-delete-btn')?.addEventListener('click', () => {
      if (this.pendingDeleteIndex === null) {
        console.error('DayView: deletion confirmed with no dive selected, nothing removed')
        return
      }
      window.store.removeDive(this.pendingDeleteIndex)
      this.pendingDeleteIndex = null
      document.getElementById('day-delete-modal')?.close()
      this.render()
    })

    // Surface intervals and labels are written on change, not on every
    // keystroke: each write cascades through the series.
    this.container.querySelectorAll('.day-interval-input').forEach(input => {
      input.addEventListener('change', e => {
        const index = parseInt(e.currentTarget.dataset.index, 10)
        // An emptied number input reports '', and Number('') is 0: read as a
        // number, a two hour interval would silently become an immediate
        // re-entry. Same conversion as the altitude and density fields.
        const raw = String(e.currentTarget.value).trim()
        const minutes = raw === '' ? Number.NaN : Number(raw)
        if (!Number.isFinite(minutes) || minutes < 0) {
          console.error('DayView: unusable surface interval "%s" for dive #%d, the previous value is kept',
            e.currentTarget.value, index + 1)
          this.render()
          return
        }
        window.store.updateDive(index, { surfaceIntervalBefore: minutes })
        this.render()
      })
    })

    this.container.querySelectorAll('.day-label-input').forEach(input => {
      input.addEventListener('change', e => {
        const index = parseInt(e.currentTarget.dataset.index, 10)
        window.store.updateDive(index, { label: e.currentTarget.value })
        // Redrawn here and not left to the store notification: on Enter the
        // field keeps the focus, and the guard of subscribe() drops the
        // redraw, leaving the plans this write just invalidated on screen
        this.render()
      })
    })
  }

  // Add the dive currently held by the plan screen to the day.
  //
  // The first dive follows nothing and is added straight away; every other one
  // needs its surface interval, asked for in the dialog of this view rather
  // than in a native prompt - it is the only value this application asks for,
  // and every other one is typed into a dialog.
  addCurrentDive() {
    const state = window.store.state

    if (!Array.isArray(state.segments) || state.segments.length === 0) {
      console.error('DayView.addCurrentDive: the plan screen holds no segment, nothing added')
      alert(this.t('day_add_needs_segments'))
      return
    }

    if (state.day.dives.length === 0) {
      this.addDiveWithInterval(null)
      return
    }

    const input = document.getElementById('day-interval-value')
    if (input) input.value = '120'
    document.getElementById('day-interval-error')?.classList.add('hidden')
    document.getElementById('day-interval-modal')?.showModal()
  }

  // Read the interval typed into the dialog, and add the dive when it is a
  // duration. An emptied field reports '', which Number() would read as an
  // immediate re-entry: the message is shown inside the dialog, and the value
  // stays there to be corrected.
  confirmAddCurrentDive() {
    const raw = String(document.getElementById('day-interval-value')?.value ?? '').trim()
    const minutes = raw === '' ? Number.NaN : Number(raw)

    if (!Number.isFinite(minutes) || minutes < 0) {
      console.error('DayView.confirmAddCurrentDive: unusable surface interval "%s", nothing added', raw)
      document.getElementById('day-interval-error')?.classList.remove('hidden')
      return
    }

    document.getElementById('day-interval-modal')?.close()
    this.addDiveWithInterval(minutes)
  }

  // Hand the dive of the plan screen to the store, with the interval that
  // precedes it (null on the first dive of the day).
  addDiveWithInterval(surfaceIntervalBefore) {
    const state = window.store.state
    const id = window.store.addDive({
      segments: state.segments,
      ccr: state.ccr?.enabled ? state.ccr : null,
      surfaceIntervalBefore
    })
    // Store.addDive logged the reason when it refused
    if (id === null) return

    this.render()
  }

  // Save the day under a name, so several days can live side by side.
  //
  // Only the inputs are saved (SavedDays): a loaded day is a day to compute
  // again, because the settings it was computed with may have changed since.
  save() {
    const name = document.getElementById('day-save-name')?.value
    const id = SavedDays.save(name, window.store.state)
    document.getElementById('day-save-modal')?.close()

    if (id === null) {
      // SavedDays.save logged the reason
      alert(this.t('saved_days_save_failed'))
      return
    }
    showToast(this.t('saved_days_saved'))
  }

  // Export the day, one page per computed dive plus the totals.
  //
  // A day where nothing has been computed exports nothing: DayExport says so in
  // the log, and the diver is told rather than left with an empty file.
  export(format) {
    const state = window.store.state
    const exporter = new DayExport(
      state.day.dives, state.settings, state.environment, state.gases, this.t.bind(this)
    )

    // Nothing computed and a document that could not be produced are two
    // different problems, and only the first one is the diver's to fix
    if (exporter.computedDives().length === 0) {
      alert(this.t('day_nothing_to_export'))
      return
    }

    const saved = format === 'pdf' ? exporter.savePdf() : exporter.saveHtml()
    if (!saved) {
      // DayExport logged the exact failure
      alert(this.t('day_export_failed'))
    }
  }

  // Compute the whole series and store what comes back.
  //
  // Restarting from the first dive that lost its results and not from the top:
  // editing the third dive of a day must not recompute the first two. planDay
  // falls back on a full recomputation, with a log, if the dives before that
  // point do not carry theirs.
  calculate() {
    const state = window.store.state
    const dives = state.day.dives
    if (dives.length === 0) {
      console.error('DayView.calculate: the day carries no dive, nothing computed')
      return
    }

    const from = dives.findIndex(dive => !dive.results)
    const shared = {
      settings: state.settings,
      environment: state.environment,
      gases: state.gases,
      diluents: state.diluents
    }

    let planned
    try {
      planned = planDay({ shared, dives, from: from < 0 ? dives.length : from })
    } catch (e) {
      // planDay only throws when the day itself is unusable; a dive the engine
      // refuses comes back as an error on that dive
      console.error('DayView.calculate: the day could not be planned:', e)
      alert(`${this.t('plan_error_title')}${e instanceof PlanError ? `\n${this.t(e.code, e.details || {})}` : ''}`)
      return
    }

    let refused = 0
    for (const { index, results, error } of planned) {
      // Through the store, which owns the day: it also clears the error of a
      // dive that plans again, and an edit clears it through updateDive
      window.store.setDiveResults(index, results, error ? (error.code || 'error') : null)
      if (error) refused++
    }

    this.render()

    if (refused > 0) {
      // Each refusal was logged by planDay with its code and its details
      alert(this.t('day_dives_refused', { count: refused }))
    }
  }
}
